"use client";

import { useState } from "react";
import { Command as CommandPrimitive } from "cmdk";
import { ArrowLeft, MagnifyingGlass } from "@phosphor-icons/react";
import { CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/shared/ui/command";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { PaletteFooter } from "../components/PaletteFooter";
import { cn } from "@/shared/lib/utils";
import type { PaletteCtx, Result, Verb } from "../types";

export function EntityPage({ ctx, result }: { ctx: PaletteCtx; result: Result }) {
  const [input, setInput] = useState("");
  const verbs = result.verbs ?? [];
  const q = input.toLowerCase().trim();
  const filtered = q ? verbs.filter((v) => v.title.toLowerCase().includes(q)) : verbs;

  const runVerb = (v: Verb) => {
    if (v.confirm) {
      ctx.push({ kind: "verb-confirm", verb: v, result });
      return;
    }
    void v.run(result.entity, ctx);
    ctx.close();
  };

  return (
    <>
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-3">
        <button
          type="button"
          onClick={ctx.pop}
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground outline-none transition-[color,background-color] duration-(--dur-fast) hover:bg-accent hover:text-foreground focus-visible:ring-(length:--ring-w) focus-visible:ring-ring"
          aria-label="Back"
        >
          <ArrowLeft className="size-4" />
        </button>
        <Breadcrumbs chain={result.scopeChain} size="md" />
        <div className="text-sm font-medium truncate ml-1">{result.title}</div>
      </div>

      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-5">
        <MagnifyingGlass className="size-5 text-muted-foreground/70 shrink-0" />
        <CommandPrimitive.Input
          autoFocus
          value={input}
          onValueChange={setInput}
          data-palette-input="true"
          placeholder="Filter actions…"
          className="flex-1 h-full bg-transparent outline-none text-[15px] placeholder:text-muted-foreground/60"
        />
      </div>

      <CommandList
        className="
          max-h-none flex-1 overflow-y-auto p-2
          [&_[cmdk-group-heading]]:px-3
          [&_[cmdk-group-heading]]:pb-1
          [&_[cmdk-group-heading]]:pt-3
          [&_[cmdk-group-heading]]:text-caption
          [&_[cmdk-group-heading]]:font-semibold
          [&_[cmdk-group-heading]]:uppercase
          [&_[cmdk-group-heading]]:tracking-wider
          [&_[cmdk-group-heading]]:text-muted-foreground/70
        "
      >
        <CommandEmpty className="py-16 text-center text-sm text-muted-foreground">No matching actions.</CommandEmpty>
        <CommandGroup heading="Actions">
          {filtered.map((v) => {
            const Icon = v.icon;
            return (
              <CommandItem
                key={v.id}
                value={`verb:${v.id} ${v.title}`}
                onSelect={() => runVerb(v)}
                className={cn(
                  "group flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-md cursor-pointer",
                  "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground",
                  v.destructive && "data-[selected=true]:bg-danger-soft data-[selected=true]:text-danger"
                )}
              >
                {Icon && (
                  <div className="flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground group-data-[selected=true]:bg-background group-data-[selected=true]:text-foreground">
                    <Icon className="size-4" />
                  </div>
                )}
                <div className="flex-1 text-[14px] font-medium leading-tight">{v.title}</div>
                {v.shortcut && (
                  <div className="font-mono text-xs text-muted-foreground/70">{v.shortcut}</div>
                )}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>

      <PaletteFooter />
    </>
  );
}
